import * as APIRoutes from '@mm/api-routes';
import { omit, pickBy } from 'lodash';
import { CreateChatbotParams } from '../actions/ChatbotCRUDActions';
import { snake } from '../helpers/converterString';
import ChatbotModel, { ChannelsMap } from '../model/ChatbotModel';
import AuthenticatedController from './AuthenticatedController';

export type CreateResponse = {
  success: boolean;
  bot: ChatbotModel;
  errors?: string[];
};

type GetAllArgs =
  | { companyId: number; light?: boolean }
  | { facebookPageId: number; light?: boolean };

export type BotCloneArgs = {
  name: string;
  async?: boolean;
  facebookPageId: number;
  targetBotId?: number; // BE expects either facebookPageId or targetBotId
} & ({ botId: number } | { ref: string }); // Either source bot or its reference is needed.

export const mapBEChatbotToFEChatbot = (
  chatbot: Omit<ChatbotModel, 'connectedPages'> & { facebookPages: any },
) =>
  ({
    ...chatbot,
    connectedPages: chatbot.facebookPages,
  } as ChatbotModel);

export default class ChatbotController extends AuthenticatedController {
  static async getChatbot(botId: number): Promise<ChatbotModel> {
    const response = await super.get(APIRoutes.BOT(botId));
    return mapBEChatbotToFEChatbot(response);
  }

  static async getAll(args: GetAllArgs): Promise<ChatbotModel[]> {
    const formattedArgs = snake(args);
    const response = await super.get(APIRoutes.BOTS, formattedArgs);
    return response.map(mapBEChatbotToFEChatbot);
  }

  static attachBotToFacebookPage(
    botId: number | null,
    facebookPageId: number,
    force?: boolean,
  ) {
    return super.put(APIRoutes.ATTACH_BOT_TO_FACEBOOK_PAGE(facebookPageId), {
      botId,
      force,
    });
  }

  static activateBot(botId: number, facebookPageId: number) {
    return super.post(APIRoutes.ACTIVATE_BOT, {
      bot_id: botId,
      facebook_page_id: facebookPageId,
    });
  }

  static postChatbot(entity: CreateChatbotParams): Promise<CreateResponse> {
    return super.post(APIRoutes.BOTS, entity);
  }

  static async putChatbot(entity: Partial<ChatbotModel>) {
    const response = await super.put(`${APIRoutes.BOTS}/${entity.id}`, entity);
    return mapBEChatbotToFEChatbot(response);
  }

  static deleteChatbot(id: number) {
    return super.delete(`${APIRoutes.BOTS}/${id}`);
  }

  static togglePlatformChannels({
    botId,
    platformChannels,
    force,
  }: {
    botId: number;
    platformChannels: Partial<ChannelsMap>;
    force?: boolean;
  }) {
    return super.put(APIRoutes.TOGGLE_PLATFORM_CHANNELS(botId), {
      platformChannels,
      force,
    });
  }

  static clone(cloneArgs: BotCloneArgs): ChatbotModel {
    // Note: BE expects either facebookPageId or targetBotId
    const argsCleanedUp = cloneArgs.targetBotId
      ? omit(cloneArgs, ['facebookPageId'])
      : cloneArgs;

    return super.post(
      APIRoutes.CLONE_BOT,
      pickBy(argsCleanedUp),
    ) as unknown as ChatbotModel;
  }

  static languages() {
    return super.get(APIRoutes.LANGUAGES);
  }

  static updateGlobalJS(botId: number) {
    return super.post(`${APIRoutes.BOTS}/${botId}/update_global_js`);
  }

  static getWebchatToken(botId: number): Promise<{ webchatToken: string }> {
    return super.get(`${APIRoutes.BOTS}/${botId}/webchat_token`);
  }
}
